'use client';

import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { TPhrasesData, TPhrasesTypes } from '@/types/types/general/phrases';
import { applyDevtools } from './storeUtils';
import { config } from '@/config';
import { setLocaleCookie } from '@/utils/locale/setLocaleCookie';

export interface PhrasesState {
    locale: {
        locale: string | undefined;
        hasBeenChecked: boolean;
    };
    phrases: TPhrasesData;
    updated: Date | null;
    setLocale: (locale: string | undefined) => void;
    setLocaleHasBeenChecked: () => void;
    setPhrases: (data?: TPhrasesData, type?: TPhrasesTypes) => Promise<void>;
    clearPhrases: (type: TPhrasesTypes) => void;
}

export const usePhrasesStore = createWithEqualityFn<PhrasesState>()(
    persist(
        applyDevtools(
            (set) => ({
                locale: {
                    locale: undefined,
                    hasBeenChecked: false,
                },
                phrases: {},
                updated: null,
                setLocale: (locale: string | undefined) => {
                    setLocaleCookie(locale);
                    set(
                        {
                            locale: {
                                locale,
                                hasBeenChecked: true,
                            },
                        },
                        false,
                        'Setting of locale',
                    );
                },
                setLocaleHasBeenChecked: () => {
                    set(
                        (state) => ({
                            ...state,
                            locale: {
                                ...state.locale,
                                hasBeenChecked: true,
                            },
                        }),
                        false,
                        'Setting of locale has been checked',
                    );
                },
                // eslint-disable-next-line @typescript-eslint/require-await
                setPhrases: async (data?: TPhrasesData, type = 'main') => {
                    set(
                        (state) => ({
                            ...state,
                            phrases: {
                                ...state.phrases,
                                [type]: { ...state.phrases[type], ...data },
                            },
                            updated: new Date(),
                        }),
                        false,
                        `Setting of ${type} phrases`,
                    );
                },
                clearPhrases: (type) => {
                    set(
                        (state) => ({
                            ...state,
                            phrases: {
                                ...state.phrases,
                                [type]: {},
                            },
                            updated: new Date(),
                        }),
                        false,
                        `Clearing of ${type} phrases`,
                    );
                },
            }),
            { name: `MPW ${config.theme} Phrases Store` },
        ),
        {
            name: `mpw-${config.theme}-phrases-storage`,
        },
    ),
    shallow,
);
